/**
 * UsageInsightsCard — "Il valore di Ando questo mese" trust-building
 * KPI strip (ported from V1, Jun 13, 2026).
 *
 * V1 reference: ``ando-frontend/app/components/billing/UsageInsightsCard.tsx``.
 * The previous V2 revision repurposed this slot for anomaly chips +
 * top cost-drivers — a useful debugging tool but the wrong tone for
 * the billing page. The user wants the V1 ROI-framing back: how much
 * time has Ando saved, what mix of power-levels did you use, and how
 * much knowledge has been ingested.
 *
 * Three cards, equal-height grid, no card chrome competition:
 *
 *   1. **Tempo risparmiato** — hero ROI metric. Hours saved derived
 *      from query × minutes-per-power-level. NEVER renders the monetary
 *      equivalent (V1 surfaced ``~€500 di valore stimato`` — violates
 *      the customer-facing rule "MAI mostrare EUR su customer-facing
 *      usage/cap surfaces, solo %"; we keep the time tell only).
 *   2. **Query totali** — count + stacked bar showing quick/standard/
 *      expert mix. The mix signals "your team uses the heavy modes" =
 *      justified spend.
 *   3. **Knowledge base** — docs indexed + storage used. Signals the
 *      lock-in value the user has built up inside Ando.
 *
 * Conservative bench:
 *   - quick    → 1.5 min (lookup of a single fact)
 *   - standard → 4   min (multi-source synthesis)
 *   - expert   → 12  min (deep analysis)
 *
 * Numbers are deliberately under-stated — credibility beats wow-factor
 * in a B2B billing context.
 */

import type { BillingUsageResponse } from "@ando/core";
import { cn } from "@ando/ui";
import { Clock, Database, Sparkles } from "lucide-react";
import type * as React from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

const MINUTES_PER_QUERY = {
  quick: 1.5,
  standard: 4,
  expert: 12,
} as const;

function computeHoursSaved(usage: BillingUsageResponse | null | undefined): number {
  if (!usage) return 0;
  const totalMinutes =
    usage.quick_queries_used * MINUTES_PER_QUERY.quick +
    usage.standard_queries_used * MINUTES_PER_QUERY.standard +
    usage.expert_queries_used * MINUTES_PER_QUERY.expert;
  return totalMinutes / 60;
}

function formatHours(hours: number): string {
  if (hours < 1) {
    const mins = Math.round(hours * 60);
    return mins === 0 ? "0 min" : `${mins} min`;
  }
  if (hours < 10) {
    return `${hours.toFixed(1).replace(".", ",")} h`;
  }
  return `${Math.round(hours)} h`;
}

function formatInt(value: number): string {
  return value.toLocaleString("it-IT");
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ─── Card primitive ───────────────────────────────────────────────────────

interface InsightCardProps {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
  readonly hint?: React.ReactNode;
  readonly accent?: boolean;
}

const InsightCard: React.FC<InsightCardProps> = ({
  icon,
  label,
  value,
  hint,
  accent,
}) => (
  <div
    className={cn(
      "flex flex-col gap-1 rounded-md border bg-card/40 p-4",
      accent
        ? "border-ando-orange/30"
        : "border-hairline",
    )}
  >
    <div
      className={cn(
        "inline-flex items-center gap-1.5 font-medium text-[12px]",
        accent ? "text-ando-orange" : "text-ink-2",
      )}
    >
      {icon}
      <span>{label}</span>
    </div>
    <p
      className={cn(
        "mt-0.5 font-display font-semibold text-[22px] tabular-nums tracking-tight",
        accent ? "text-ando-orange" : "text-ink-1",
      )}
    >
      {value}
    </p>
    {hint ? (
      <div className="mt-1 text-[11.5px] text-ink-3 leading-relaxed">
        {hint}
      </div>
    ) : null}
  </div>
);
InsightCard.displayName = "UsageInsightsCard.InsightCard";

// ─── Query mix stacked bar (inside the Query totali card) ────────────────

interface QueryMixBarProps {
  readonly quick: number;
  readonly standard: number;
  readonly expert: number;
}

const QueryMixBar: React.FC<QueryMixBarProps> = ({ quick, standard, expert }) => {
  const { i18n } = useLingui();
  const total = quick + standard + expert;
  if (total === 0) {
    return (
      <span className="text-[11.5px] text-ink-3">
        <Trans>Nessuna query ancora questo mese.</Trans>
      </span>
    );
  }
  const safeTotal = total || 1;
  const pctQuick = (quick / safeTotal) * 100;
  const pctStandard = (standard / safeTotal) * 100;
  const pctExpert = (expert / safeTotal) * 100;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <div
        role="img"
        aria-label={i18n._(
          msg`Distribuzione query: ${Math.round(pctQuick)}% rapide, ${Math.round(pctStandard)}% standard, ${Math.round(pctExpert)}% expert`,
        )}
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-ink-1/[0.06]"
      >
        <div className="bg-success" style={{ width: `${pctQuick}%` }} />
        <div className="bg-ando-orange" style={{ width: `${pctStandard}%` }} />
        <div className="bg-warning" style={{ width: `${pctExpert}%` }} />
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-ink-3">
        <li className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-success" aria-hidden />
          <span className="tabular-nums">
            {Math.round(pctQuick)}% <Trans>rapide</Trans>
          </span>
        </li>
        <li className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-ando-orange" aria-hidden />
          <span className="tabular-nums">
            {Math.round(pctStandard)}% <Trans>standard</Trans>
          </span>
        </li>
        <li className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-warning" aria-hidden />
          <span className="tabular-nums">
            {Math.round(pctExpert)}% <Trans>expert</Trans>
          </span>
        </li>
      </ul>
    </div>
  );
};
QueryMixBar.displayName = "UsageInsightsCard.QueryMixBar";

// ─── Main component ──────────────────────────────────────────────────────

export interface UsageInsightsCardProps {
  readonly usage?: BillingUsageResponse | null;
}

export const UsageInsightsCard: React.FC<UsageInsightsCardProps> = ({ usage }) => {
  const { i18n } = useLingui();

  const hoursSaved = computeHoursSaved(usage);
  const hasTimeValue = hoursSaved > 0;
  const totalQueries =
    (usage?.quick_queries_used ?? 0) +
    (usage?.standard_queries_used ?? 0) +
    (usage?.expert_queries_used ?? 0);
  const docs = usage?.documents_indexed ?? 0;
  const storage = usage?.storage_used_bytes ?? 0;

  // Empty-state copy is context-sensitive:
  //   - No docs AND no queries → onboarding nudge ("Inizia a usare Ando").
  //   - Docs indexed but no queries → ingestion was done but chat is
  //     untouched — invite to actually USE the knowledge base, since
  //     "inizia a usare Ando" reads wrong to a user who's already
  //     uploaded 9000 documents (Jun 13, 2026 user feedback).
  const timeHint = hasTimeValue ? (
    <Trans>Stima conservativa: quick 1,5 min · standard 4 min · expert 12 min.</Trans>
  ) : docs > 0 ? (
    <Trans>
      Hai già una base di conoscenza pronta. Fai la prima domanda alla chat per
      iniziare a vedere il valore generato.
    </Trans>
  ) : (
    <Trans>Inizia a usare Ando per vedere il valore generato.</Trans>
  );

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <InsightCard
        accent
        icon={<Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />}
        label={i18n._(msg`Tempo risparmiato`)}
        value={hasTimeValue ? formatHours(hoursSaved) : "—"}
        hint={timeHint}
      />
      <InsightCard
        icon={<Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />}
        label={i18n._(msg`Query totali`)}
        value={formatInt(totalQueries)}
        hint={
          totalQueries > 0 ? (
            <QueryMixBar
              quick={usage?.quick_queries_used ?? 0}
              standard={usage?.standard_queries_used ?? 0}
              expert={usage?.expert_queries_used ?? 0}
            />
          ) : docs > 0 ? (
            <Trans>
              Pronto a partire: chat con la tua knowledge base per popolare la
              metrica.
            </Trans>
          ) : (
            <Trans>Nessuna query ancora questo mese.</Trans>
          )
        }
      />
      <InsightCard
        icon={<Database className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />}
        label={i18n._(msg`Knowledge base`)}
        value={formatInt(docs)}
        hint={
          docs > 0 ? (
            <Trans>
              documenti indicizzati ·{" "}
              <span className="tabular-nums">{formatBytes(storage)}</span> nel
              cervello
            </Trans>
          ) : (
            <Trans>
              Carica un documento o connetti una sorgente per iniziare a
              costruire la knowledge base.
            </Trans>
          )
        }
      />
    </div>
  );
};
UsageInsightsCard.displayName = "UsageInsightsCard";
